import LegalDoc from "../components/LegalDoc";

const CONTENT = `# End User License Agreement

**RoofNuts LLC — RoofNuts Calling Portal**

**Effective:** August 1, 2026

This End User License Agreement ("Agreement") governs your use of the RoofNuts Calling Portal at
roofnutscalling.com (the "Portal"), provided by RoofNuts LLC ("RoofNuts," "we," "us"). By accessing
or using the Portal, you agree to this Agreement. If you do not agree, do not use the Portal.

The Portal is private business software. It is not offered to the general public and is not sold or
licensed as a standalone product.

## 1. License

RoofNuts grants you a limited, non-exclusive, non-transferable, revocable license to access and use
the Portal for its intended business purpose, for as long as your account remains active and you
comply with this Agreement.

## 2. Accounts

Accounts are created by RoofNuts administrators. There is no public or self-service registration.

You are responsible for keeping your credentials confidential and for all activity under your
account. Do not share your login. Notify us promptly at admin@roofnuts.com if you believe your
account has been compromised.

RoofNuts may suspend or terminate any account at any time, including for violation of this
Agreement.

## 3. Acceptable use

You agree not to:

- access data belonging to another client, or attempt to circumvent role-based access controls;
- reverse engineer, decompile, copy, or create derivative works from the Portal;
- use automated means to scrape or extract data except through features we provide;
- interfere with or disrupt the Portal, or attempt to gain unauthorized access to it or its
  underlying systems;
- use the Portal to violate any law, including laws governing telephone solicitation, text
  messaging, and call recording;
- upload malicious code or unlawful content; or
- resell, sublicense, or provide access to the Portal to any third party.

## 4. Data and ownership

RoofNuts and its licensors own the Portal, including all software, design, and content, and all
associated intellectual property rights. This Agreement grants no ownership.

Data you enter into the Portal remains yours as between you and RoofNuts. You grant us the right to
host, process, and display that data as needed to provide the service. Campaign and appointment data
generated by RoofNuts in performing services is governed by our service agreement with you.

Our handling of personal information is described in our Privacy Policy at
roofnutscalling.com/privacy, which is incorporated into this Agreement.

## 5. Third-party services

The Portal integrates with third-party services including QuickBooks Online (Intuit), Twilio, Google,
and our payment provider. Your use of those integrations may also be subject to those providers'
terms. We are not responsible for third-party services, their availability, or their acts or
omissions.

The QuickBooks Online connection is authorized by a RoofNuts administrator and may be disconnected at
any time from within the Portal.

## 6. Availability

We aim to keep the Portal available but do not guarantee uninterrupted access. We may modify,
suspend, or discontinue any part of the Portal, and may perform maintenance, with or without notice.

## 7. Disclaimer of warranties

THE PORTAL IS PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING ANY IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE,
OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE PORTAL WILL BE ERROR-FREE, SECURE, OR UNINTERRUPTED,
OR THAT DATA DISPLAYED WILL BE COMPLETE OR ACCURATE.

Invoice and payment information shown in the Portal is a convenience copy synchronized from
QuickBooks Online. **QuickBooks Online is the system of record for all billing and payment
information.** Where the two differ, QuickBooks Online controls.

## 8. Limitation of liability

TO THE MAXIMUM EXTENT PERMITTED BY LAW, ROOFNUTS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS, REVENUE, DATA, OR BUSINESS
INTERRUPTION, ARISING FROM OR RELATED TO THE PORTAL, EVEN IF ADVISED OF THE POSSIBILITY.

OUR TOTAL AGGREGATE LIABILITY ARISING FROM OR RELATED TO THE PORTAL WILL NOT EXCEED THE AMOUNTS YOU
PAID TO ROOFNUTS FOR SERVICES IN THE THREE (3) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM.

## 9. Indemnification

You agree to indemnify and hold harmless RoofNuts and its officers, members, and employees from any
claims, damages, losses, and reasonable attorneys' fees arising from your use of the Portal, your
data, or your violation of this Agreement or applicable law.

## 10. Relationship to service terms

This Agreement covers your use of the Portal software only. RoofNuts' appointment-setting services —
including appointment credits, qualification criteria, weather thresholds, and scheduling policies —
are governed by the separate RoofNuts Terms & Conditions that clients accept within the Portal. In
the event of a conflict regarding services, those Terms & Conditions control.

## 11. Term and termination

This Agreement applies while your account is active. We may terminate or suspend your access at any
time. On termination, your license ends immediately and you must stop using the Portal. Sections 4,
7, 8, 9, and 12 survive termination.

## 12. Governing law

This Agreement is governed by the laws of the State of Georgia, without regard to its conflict of
laws rules. The exclusive venue for any dispute is the state or federal courts located in the
State of Georgia.

## 13. Changes

We may update this Agreement. Material changes will be posted at this URL with a revised effective
date. Continued use of the Portal after changes take effect constitutes acceptance.

## 14. Entire agreement

This Agreement, together with the Privacy Policy and any applicable service agreement, is the entire
agreement between you and RoofNuts regarding the Portal. If any provision is unenforceable, the rest
remains in effect. Our failure to enforce any provision is not a waiver.

## 15. Contact

**RoofNuts LLC**

admin@roofnuts.com
`;

const Eula = () => <LegalDoc content={CONTENT} />;

export default Eula;
